/**
 * The host bridge protocol (PRD B7a, B8a.6).
 *
 * Native embedding relocates the trust boundary: a host rendering `@chat42/react`
 * can read decrypted content and the access token from its own memory. That is
 * acceptable for a first-party surface and stated plainly rather than left to be
 * discovered. Where the host is *not* trusted with conversation content, the
 * iframe is the correct depth — content and tokens stay inside the frame origin,
 * and the host talks to it only through the messages below.
 *
 * Everything crossing this boundary is therefore deliberately narrow. The host
 * can ask for a session, hand over an entry context and a theme, and be told what
 * happened. It cannot read the transcript.
 */

/**
 * Raised only for a change one side cannot read. A new optional field is not one: each side
 * reads only what it knows. Neither is a new message: `isHostMessage` and `isFrameMessage` refuse
 * a type they do not know, so an older frame drops `set-model` without acting on it. Bumping for
 * those would make every host and frame refuse each other until both had shipped.
 */
export const PROTOCOL_VERSION = 1;

/**
 * What the host says before either side has spoken: the sandbox it gave the
 * frame.
 *
 * A page cannot read the sandbox of the frame it is in, and cannot learn it
 * by trying. A file a page asks the browser to keep, from a frame that does
 * not allow it, is dropped without a word: the call returns exactly as it
 * does when the file is kept (seen in Chrome, ADR 0066). So a framed page
 * told a person their document had been saved when nothing had. The host is
 * the only one that knows, because it wrote the attribute, and it says so in
 * the frame's name, which is the one thing of the host's a frame can read
 * without asking.
 *
 * Not a message: a message needs the bridge up and an origin agreed, and
 * this is wanted at the first press. Not the frame's address: the app
 * rewrites its address, and a sign-in round trip would lose it. A frame
 * keeps its name for as long as it keeps its sandbox.
 *
 * It grants nothing. It is the host describing what it did, and a host that
 * describes it wrongly misleads only the readers of its own page. The other
 * half of the exchange is the runtime's (`artifacts/handover.ts`), and a
 * test in `@chat42/react` holds the two to the same words.
 */
export const FRAME_SANDBOX = 'chat42:sandbox';

/** The name a frame is given: that word, then the sandbox's tokens. */
export const frameName = (sandbox: readonly string[]): string => [FRAME_SANDBOX, ...sandbox].join(' ');

/**
 * Host → frame.
 *
 * `surfaces` in `init` are the parts of Chat42 the host wants drawn, each a boolean, and every key
 * absent means the frame's default (`ALL_SURFACES` in `@chat42/react`, with `frameSurfaces` over
 * it). A frame ignores a key it does not know, which is why a new key needs no version. The keys a
 * host's panel usually sends:
 *
 * - `rail`: the list of conversations at the left. Not the side rail.
 * - `account` (off in a frame): the signed-in person's chip (ADR 0099).
 * - `sideRail` (on): the side rail at the conversation's right edge; off keeps the header's panel
 *   buttons instead. `sideRailOpen` (off in a frame): start it at its summary rather than its strip
 *   (ADR 0104).
 * - `title` (on): the conversation's title row. A host that names the scope in its own header
 *   sends `false`; the row's facts and doors move into the side rail.
 * - `model` (on): the composer's Model chip. A host that chooses the model through `set-model`
 *   sends `false`; with a side rail the tool count and Where move into it too, leaving Mode.
 */
export type HostMessage =
  | {
    type: 'chat42:init'; version: number; token: string; theme?: Record<string, string>; surfaces?: Record<string, boolean>;
    /** How turns are drawn from the start (`ChatView`). Absent is `detail`, as every frame drew before. */
    view?: ChatView;
    /**
     * Who answers, in the host's words: a name ("Arthur") and a mark of one or two letters ("A").
     * The conversation view draws the mark beside the agent's bubbles and the name in the box's
     * hint (ADR 0142). The engine never names itself to a host's people.
     */
    assistant?: { name: string; monogram?: string };
    /** How each reply's cost is shown from the start (`ReplyCost`). Absent is `on-demand` (ADR 0145). */
    replyCost?: ReplyCost;
    /**
     * The host answers `person-action` (ADR 0147): it makes the person's own writes, after its own
     * confirmation. Absent, the frame draws such a card with no button and says where to do it.
     */
    personActions?: true;
  }
  | OpenSession
  | NewSession
  /**
   * Put the open session on another model. No turn is taken and nothing is spent: the next
   * turn the person asks for runs on it. The frame answers with `session-changed` carrying the
   * model the next turn will use, which is how a host's header learns the change took.
   */
  | { type: 'chat42:set-model'; model: string }
  /**
   * Where the person is in the host's page now: a screen, a record, a selection, a view, in the
   * host's own names. It replaces the last one; it is not a turn and spends nothing. The next
   * turn is told it as context, apart from what the session is about, so a session that follows
   * the person around a page stays one session (ADR 0097). Any scope may send it.
   */
  | { type: 'chat42:location'; context: Record<string, string> }
  /** What the user is looking at, so a conversation opened from a host card
   *  starts already knowing about that card (PRD B8a.5, entry context). */
  | { type: 'chat42:entry-context'; context: Record<string, string> }
  | { type: 'chat42:set-theme'; theme: Record<string, string> }
  /**
   * Draw the conversation another way (ADR 0142). Only where `ready` listed `conversation-view`; an
   * older frame drops it unread. A redraw: nothing is sent, restarted or spent.
   */
  | { type: 'chat42:set-view'; view: ChatView }
  /**
   * Show each reply's cost another way (ADR 0145). Only where `ready` listed `reply-cost`; an older
   * frame drops it unread. A redraw: nothing is sent, restarted or spent.
   */
  | { type: 'chat42:set-reply-cost'; replyCost: ReplyCost }
  /**
   * What a reply's "Show the detail" does (ADR 0146): `turn`, open that turn in place, as ADR 0142
   * drew it and every frame does until told otherwise; `view`, say `detail-asked` to the host, which
   * switches the whole view and remembers it. Only where `ready` listed `detail-asks-view`.
   */
  | { type: 'chat42:set-detail'; opens: DetailOpens }
  /**
   * What the person's next message is about, as the host says it: the one thing they pressed "Talk it
   * through" on (Rule42 Plan 16, C2). The frame draws it as a chip above the box, which the person can
   * remove; `null` takes it away. Only where `ready` listed `focus-chip`. Not a turn, and nothing is
   * spent: the host says the same thing in `location`, which is what the next turn is told.
   */
  | { type: 'chat42:set-focus'; focus: { title: string } | null }
  | { type: 'chat42:token'; token: string }
  | StartWork
  | PickFolder
  | PersonActionResult
  /**
   * The open session's permission mode, as the host's own setting for the person decides it (ADR
   * 0145): `manual` or `auto`. Only where `ready` listed `permission-mode`. Not a turn; nothing is
   * spent. The service's organisation ceiling still holds under whatever mode is set.
   */
  | { type: 'chat42:set-permission-mode'; mode: 'manual' | 'auto' };

/**
 * The person pressed a button in the conversation that only the host can carry out (ADR 0147):
 * their own write, which the agent was refused, or the Undo of a change it made. Frame → host.
 *
 * The host checks `action` is a write it makes (its door, and the record the page is about), asks
 * its own confirmation where it has one, makes it as the person, and answers with
 * `person-action-result` carrying the same `requestId`. `label` is the button's words, for the
 * host's confirmation to quote; a host never trusts it to say what the write does.
 */
export interface PersonAction {
  type: 'chat42:person-action';
  requestId: string;
  label: string;
  /** The door and the record it is on: `prd-course` with `uowId`, `policy-run` with `projectId`, or `idea-run` with neither. */
  action: { door: string; uowId?: string; projectId?: string; body: Record<string, unknown> };
}

/** The host's answer to `person-action`: made or not, and its own sentence for the person. Host → frame. */
export interface PersonActionResult {
  type: 'chat42:person-action-result';
  requestId: string;
  ok: boolean;
  said: string;
}

/**
 * How the frame draws a conversation (ADR 0142).
 *
 * - `detail`: every step: the person's messages, the replies, thinking, each tool call with its
 *   timing, retries and the engine's own errors. What every frame drew before there was a choice.
 * - `conversation`: the person's messages and the replies as bubbles, the agent's one-sentence
 *   updates between steps, the cards a person must answer, and one working line while a step runs.
 *   Tool calls, thinking, timings and error text are folded away, a turn at a time behind "Show the
 *   detail".
 *
 * A view is presentation only. Both are drawn from the same journal, so they cannot disagree, and
 * switching never sends, restarts or costs anything.
 */
export type ChatView = 'conversation' | 'detail';
export const CHAT_VIEWS: readonly ChatView[] = ['conversation', 'detail'];
export const isChatView = (v: unknown): v is ChatView => v === 'conversation' || v === 'detail';

/**
 * How the conversation view shows what each reply cost (ADR 0145). A person's setting, held by the
 * host, as the view is.
 *
 * - `on-demand`: a cost button in the row that appears over a reply opens what the organisation
 *   was charged for it, the share of its input read from cache, and the model.
 * - `inline`: the same, and a line under every reply with the charge and the cached share.
 *
 * Presentation only: both read the charges recorded for the reply's model calls, and switching
 * never sends, restarts or costs anything.
 */
export type ReplyCost = 'on-demand' | 'inline';

/** What a reply's "Show the detail" opens (ADR 0146): that turn in place, or, by asking the host, the whole view. */
export type DetailOpens = 'turn' | 'view';
export const isDetailOpens = (v: unknown): v is DetailOpens => v === 'turn' || v === 'view';
export const REPLY_COSTS: readonly ReplyCost[] = ['on-demand', 'inline'];
export const isReplyCost = (v: unknown): v is ReplyCost => v === 'on-demand' || v === 'inline';

/**
 * Ask the person for a folder on one of their paired computers.
 *
 * The frame draws its own picker, over the conversation and inside the frame: the signed-in
 * person's machines, each with whether it is online, then a folder on the one they choose,
 * browsed through that machine as the Machines screen and Where already browse it. It answers
 * with exactly one of `folder-picked`, `folder-pick-cancelled` and `folder-pick-failed`, carrying
 * the same `requestId`.
 *
 * The host learns a frame can do this from `features` on `chat42:ready`. An older frame drops
 * the message unread, so a host that hears nothing within ten seconds says the picker is not
 * available yet rather than waiting.
 *
 * It grants nothing. The machines listed are the person's own, the folder is read through the
 * permissions their machine already has (ADR 0020), and what comes back is a place the person
 * chose, for the host to keep for that person and to hand back in `start-work`'s `code`.
 */
export interface PickFolder {
  type: 'chat42:pick-folder';
  /** The host's own id for this request, echoed in the answer. */
  requestId: string;
  hint?: {
    /**
     * The repository the host expects the folder to be a checkout of. The picker says whether
     * the chosen folder's remote matches it, and lets the person choose a folder that does not.
     */
    repository?: string;
    /** What the folder is for, in the host's words, shown at the top of the picker. */
    label?: string;
  };
}

/**
 * The frame's features a host may rely on, as `chat42:ready` lists them. `open-read-only` is the
 * replay contract (`session-replay.contract.json`, byte-identical in rule42.ai; ADR 0135).
 * `ask-line`: every `start-work` waits its turn in the conversation's line on the server and is
 * answered with `work-queued` or `work-started` (ADR 0137). `replace-session`: `new-session` takes
 * `replace` for Start over (ADR 0137). `conversation-view`: `set-view` and `init`'s `view`, and
 * `start-work`'s `label` (ADR 0142). `reply-cost`: `set-reply-cost` and `init`'s `replyCost`, and
 * the row of icons over each reply (ADR 0145). `detail-asks-view`: `set-detail`, and `detail-asked` when
 * a reply's detail is pressed under `view` (ADR 0146). `focus-chip`: `set-focus`, and `focus-cleared`
 * when the person removes the chip (Rule42 Plan 16, C2).
 */
export const FRAME_FEATURES = ['pick-folder', 'open-read-only', 'ask-line', 'replace-session', 'conversation-view', 'reply-cost', 'detail-asks-view', 'person-action', 'permission-mode', 'focus-chip', 'reads-code-only'] as const;

/** `person-action` is sent, and `person-action-result` read (ADR 0147). */
export const PERSON_ACTION_FEATURE = 'person-action';
/** `set-permission-mode` is honoured (ADR 0147). */
export const PERMISSION_MODE_FEATURE = 'permission-mode';

/** `set-view` and `init`'s `view` are honoured, and `start-work`'s `label` is drawn (ADR 0142). */
export const CONVERSATION_VIEW_FEATURE = 'conversation-view';
/** `set-reply-cost` and `init`'s `replyCost` are honoured (ADR 0145). */
export const REPLY_COST_FEATURE = 'reply-cost';
/** `set-detail` is honoured, and a reply's detail is said with `detail-asked` under `view` (ADR 0146). */
export const DETAIL_ASKS_VIEW_FEATURE = 'detail-asks-view';
/** `set-focus` is drawn as a chip above the box, and its removal said with `focus-cleared` (Rule42 Plan 16, C2). */
export const FOCUS_CHIP_FEATURE = 'focus-chip';
/** `start-work`'s `readsCodeOnly` is honoured: that work's turn may read the code and change none of it (2026-11-01). */
export const READS_CODE_ONLY_FEATURE = 'reads-code-only';

/** `start-work` waits its turn on the server and is answered `work-queued` or `work-started` (ADR 0137). */
export const ASK_LINE_FEATURE = 'ask-line';
/** `new-session` takes `replace`: Start over keeps the scope to one conversation (ADR 0137). */
export const REPLACE_SESSION_FEATURE = 'replace-session';

/** The replay contract's feature: `open-session` with `readOnly` is honoured. */
export const READ_ONLY_OPEN_FEATURE = 'open-read-only';

/**
 * Open a session the host stored the id of.
 *
 * `model` and `origin` are optional and mean what they mean on `new-session`. They are used
 * only where the stored id is gone and the frame makes the session again: without them that
 * session came back on no model the person chose and under no origin, so the next
 * `new-session` with the origin made a second conversation instead of finding this one. A
 * session that is found keeps its own model; `set-model` or `start-work` change it.
 */
export interface OpenSession {
  type: 'chat42:open-session';
  sessionId: string;
  model?: string;
  origin?: string;
  /**
   * Show the session, and nothing more (ADR 0135). Never makes a session: an id that is not there
   * is answered with `session-missing`, and `model` and `origin` are ignored. Never writes to it:
   * no about, no code attached, no work, model or location taken while it is open read only.
   */
  readOnly?: true;
}

/** A read-only open found no session by that id, and made none (ADR 0135). */
export interface SessionMissing {
  type: 'chat42:session-missing';
  sessionId: string;
}

/**
 * Make a session for what the host has on screen, or find the one already made for it.
 *
 * `model` is the one the person chose before asking. It is set on the session as it is
 * created, so the first turn runs on it and a reopen has nothing to ask.
 *
 * `origin` is the host's own name for the scope this session serves, such as one person's
 * conversation about one record. With it, `new-session` is find-or-create: the caller's own
 * session for that origin is opened if there is one, and made with it if there is not, so two
 * tabs, or a host that lost the id it stored, reach the same conversation. A session for that
 * origin that belongs to somebody else is never returned. Without it, every `new-session` is a
 * new conversation, as it always was.
 */
export interface NewSession {
  type: 'chat42:new-session';
  title?: string;
  model?: string;
  origin?: string;
  /**
   * Start over, with `origin` (ADR 0137): the id of the conversation on screen. It is finished, with
   * whatever else of the person's holds the origin, and the new conversation is made under the
   * origin, so the scope keeps one conversation and find-or-create finds the new one. Only where the
   * frame lists `replace-session`; an older frame ignores it.
   */
  replace?: string;
}

/**
 * Set the session up for a piece of work and start it: a model, a reasoning
 * preset, how much it may do on its own, then the first message.
 *
 * Applied to the session the frame ends up on — the one the host opened, or
 * the one it made — and only once that session's code is attached, so the
 * first turn of a run about a repository is not taken without the repository.
 * Sent beside `open-session` or `new-session`, in any order; the frame holds it
 * until there is a session ready to take it.
 */
export interface StartWork {
  type: 'chat42:start-work';
  prompt: string;
  /**
   * What the person pressed, in their words ("Write the next sections"), at most 120 characters.
   * The conversation view draws it as the person's message in place of `prompt`, which is written
   * for the model and may name tools (ADR 0142). The model reads `prompt` alone. Absent, the view
   * says only that work was started from the page.
   */
  label?: string;
  model?: string;
  /** A reasoning preset's id. */
  preset?: string;
  /** `plan` | `manual` | `acceptEdits` | `auto` | `bypass`. */
  permissionMode?: string;
  /**
   * Toolsets of the host's own service to put in front of the model from now on, such as
   * `['plan', 'execute']` for a button that carries a plan out. Added to the session's focus
   * before the prompt runs, never taken from it: a focus offers tools first and removes none
   * (ADR 0049), so a session that was given a toolset keeps it.
   */
  focus?: string[];
  /**
   * A folder on one of the person's paired machines, as `folder-picked` named it, attached to
   * the session before the first turn. With `readOnly` the session's tools may read the folder
   * and not change it, for this session. A machine that is offline, or a folder that is no
   * longer there, is answered with `chat42:error` (`case` `machine-offline` or `folder-gone`)
   * and no turn is taken: work about some code never starts without it.
   */
  code?: { deviceId: string; path: string; readOnly?: boolean };
  /**
   * This work reads the code and changes none of it, for its own turn: every repository open in
   * the conversation gives the reading tools only, and a call to change one is refused with these
   * words. One line saying why ("Policy edits never change code."), or `true` for the plain words.
   * For work about something other than the code, sent into a conversation that has the code open
   * for writing (2026-11-01).
   */
  readsCodeOnly?: string | true;
}

/** Frame → host. Notifications only: never conversation content. */
export type FrameMessage =
  /**
   * `features` are the optional things this frame can do, from `FRAME_FEATURES`. Absent from a
   * frame older than the list, which a host reads as none of them.
   */
  | { type: 'chat42:ready'; version: number; features?: string[] }
  | FolderPicked
  | FolderPickCancelled
  | FolderPickFailed
  | SessionChanged
  | { type: 'chat42:needs-attention'; count: number }
  | { type: 'chat42:token-expired' }
  | FrameError
  /** The work asked for with `start-work` was sent to this session. `askId`: which ask (ADR 0137). */
  | { type: 'chat42:work-started'; sessionId: string; askId?: string }
  | WorkQueued
  | WorkDropped
  | SessionMissing
  /**
   * The person pressed "Show the detail" in a frame told `set-detail` `view` (ADR 0146). The host
   * switches to full detail (`set-view`), so the frame changes nothing itself. Carries nothing of the
   * conversation.
   */
  | { type: 'chat42:detail-asked'; sessionId: string }
  /**
   * The person removed the focus chip (`set-focus`): the next message is about the screen as a whole.
   * The host drops the thing from its `location`. The frame has already taken the chip away.
   */
  | { type: 'chat42:focus-cleared' }
  | PersonAction;

/**
 * The work asked for with `start-work` is waiting its turn in this session's line on the server
 * (ADR 0137): Arthur is busy with another turn, or waits on the person (a question, an approval, a
 * sign-in). `position` is 1 for the next to start. Said again when the place changes, and followed
 * by `work-started` once its turn begins, while the frame is open. Held on the server, so it starts
 * whether or not a frame is open then. `merged`: the same work was already waiting or under way,
 * and this ask was folded into it rather than run twice.
 */
export interface WorkQueued {
  type: 'chat42:work-queued';
  sessionId: string;
  position: number;
  merged?: true;
  /** Which ask, so a host following several tells them apart. The first ask's, where it merged. */
  askId?: string;
}

/**
 * The work asked for with `start-work` left the line without starting (ADR 0137): taken back
 * (`withdrawn`, as Start over does) or refused as it was about to start (`failed`, said in the
 * conversation). Never followed by `work-started`. The host shows it as not started.
 */
export interface WorkDropped {
  type: 'chat42:work-dropped';
  sessionId: string;
  askId: string;
  how: 'withdrawn' | 'failed';
}

/**
 * The folder the person chose, in answer to `pick-folder`.
 *
 * `deviceName` and `path` are the person's own: a host may keep them for that person and nobody
 * else. `remote` and `branch` are where the folder is a git checkout. `matches` is sent only where
 * the request named a repository: whether the folder's remote is that repository, compared the
 * way a host compares repository addresses (scheme, user, `git@host:` and `.git` set aside). The
 * person may choose a folder that does not match; the picker told them so first.
 */
export interface FolderPicked {
  type: 'chat42:folder-picked';
  requestId: string;
  deviceId: string;
  deviceName: string;
  path: string;
  remote?: string;
  branch?: string;
  matches?: boolean;
}

/** The person closed the picker without choosing. */
export interface FolderPickCancelled {
  type: 'chat42:folder-pick-cancelled';
  requestId: string;
}

/**
 * No folder could be chosen. `no-machine`: the person has no paired machine and did not pair one.
 * `offline`: the only machines they have are offline. `not-allowed`: this frame may not use a
 * machine for them. `other`: anything else.
 */
export interface FolderPickFailed {
  type: 'chat42:folder-pick-failed';
  requestId: string;
  case: 'no-machine' | 'offline' | 'not-allowed' | 'other';
}

/**
 * Which session the frame is showing, and what a host's header needs to say about it.
 *
 * `model` is the model the next turn will run on, after the session's own choice, the person's
 * setting and the deployment's default have each had their say: null only where there is none
 * at all, and absent where the frame could not find out. `turns` is how many times the person
 * has spoken in it, the turn in flight included, so a host can offer ways to begin only while
 * nobody has. Both are sent when the session opens, after `set-model`, and after every turn.
 */
export interface SessionChanged {
  type: 'chat42:session-changed';
  sessionId: string;
  title: string;
  state: string;
  model?: string | null;
  turns?: number;
}

/**
 * Something the host should know went wrong.
 *
 * `message` is Chat42's own sentence, for a host with nothing better to say. Where the error is
 * about code that could not be attached, `case` is which way it failed (`attachTrouble`'s cases:
 * `no-token`, `no-access`, `sign-in-refused`, `repo-gone`, `folder-gone`, `other` and the rest,
 * and `machine-offline` for a `start-work` whose `code` is on a machine that is not connected)
 * and `repository` the address it is about, so a host can say it in its own words and never has
 * to show the paragraph. Several repositories can be in one message; the case is the first's,
 * which is the record's primary where the primary is one of them.
 */
export interface FrameError {
  type: 'chat42:error';
  message: string;
  case?: string;
  repository?: string;
}

/**
 * Shape check only. It establishes that the payload is one of ours; the origin
 * check establishes that it is from someone allowed to send it. Neither is
 * sufficient alone, and both run on every message.
 */
const looksLikeOurs = (v: unknown): v is { type: string } =>
  typeof v === 'object' && v !== null &&
  typeof (v as { type?: unknown }).type === 'string' &&
  (v as { type: string }).type.startsWith('chat42:');

const HOST_TYPES = new Set<HostMessage['type']>([
  'chat42:init', 'chat42:open-session', 'chat42:new-session', 'chat42:set-model', 'chat42:location',
  'chat42:entry-context', 'chat42:set-theme', 'chat42:set-view', 'chat42:set-reply-cost', 'chat42:set-detail', 'chat42:token', 'chat42:start-work',
  'chat42:pick-folder', 'chat42:person-action-result', 'chat42:set-permission-mode', 'chat42:set-focus',
]);

const FRAME_TYPES = new Set<FrameMessage['type']>([
  'chat42:ready', 'chat42:session-changed', 'chat42:needs-attention',
  'chat42:token-expired', 'chat42:error', 'chat42:work-started', 'chat42:work-queued', 'chat42:work-dropped',
  'chat42:folder-picked', 'chat42:folder-pick-cancelled', 'chat42:folder-pick-failed', 'chat42:session-missing',
  'chat42:detail-asked', 'chat42:focus-cleared',
  'chat42:person-action',
]);

export const isHostMessage = (v: unknown): v is HostMessage =>
  looksLikeOurs(v) && HOST_TYPES.has(v.type as HostMessage['type']);

export const isFrameMessage = (v: unknown): v is FrameMessage =>
  looksLikeOurs(v) && FRAME_TYPES.has(v.type as FrameMessage['type']);
